import React, { useEffect, useState } from 'react';
import { API } from 'utilities/api';

/**
 * "Coming soon" placeholder page.
 *
 * Rendered for any subdomain on a brand's custom domain that the
 * owner hasn't pointed at a page or the storefront yet — the brand
 * resolver attaches `req.resolvedRoute.type = 'coming_soon'`, the
 * frontend's catch-all route mounts this component, and the resolver
 * also seeds `window.__TPO_BRAND__` so we can read the brand name +
 * color on first paint without an extra round trip.
 *
 * Falls back to a generic message when the brand can't be resolved
 * (misconfigured DNS, dev preview, etc.).
 *
 * Deliberately says nothing about TechProOne and nothing about the site's
 * admin state. This page is served on the brand's OWN domain to the brand's
 * own visitors: a platform byline advertises us on someone else's address,
 * and "the owner hasn't set this up yet" tells a customer about the internal
 * configuration rather than the only thing they care about — that there is
 * nothing here yet and it is worth returning.
 */
export default function ComingSoon() {
  const [brand, setBrand] = useState(() => {
    if (typeof window !== 'undefined' && window.__TPO_BRAND__) return window.__TPO_BRAND__;
    return null;
  });

  useEffect(() => {
    if (brand) return;
    // Best-effort. The public endpoint at `/dashboard/brand/profile/findBrandByDomain`
    // resolves the brand from the Host header server-side; if the
    // request errors (no JWT, unverified domain, dev preview) we just
    // keep the generic copy.
    API.getBrandByDomain?.().then((b) => b && setBrand(b)).catch(() => { /* keep generic */ });
  }, [brand]);

  const accent = brand?.brand_color || '#4F46E5';
  const name = brand?.name || 'This site';

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px',
        background: `radial-gradient(circle at 30% 20%, ${accent}22, transparent 50%), radial-gradient(circle at 70% 80%, ${accent}33, transparent 50%), #0b0d1a`,
        color: '#e5e7eb',
        fontFamily: "'Inter', -apple-system, sans-serif",
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 560 }}>
        {brand?.logo_upload_path && (
          <img
            src={brand.logo_upload_path}
            alt={name}
            style={{ maxHeight: 80, marginBottom: 28, filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.4))' }}
          />
        )}
        <div
          style={{
            display: 'inline-block',
            padding: '6px 14px',
            borderRadius: 999,
            background: accent,
            color: '#fff',
            fontWeight: 600,
            fontSize: 12,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            marginBottom: 24,
          }}
        >
          Under construction
        </div>
        <h1
          style={{
            fontSize: 'clamp(32px, 6vw, 56px)',
            margin: '0 0 16px 0',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
          }}
        >
          {name} is under construction.
        </h1>
        <p
          style={{
            fontSize: 17,
            color: '#9ca3af',
            margin: '0 auto 28px auto',
            maxWidth: 460,
            lineHeight: 1.55,
          }}
        >
          We're putting the finishing touches on this site. Please check back soon.
        </p>
      </div>
    </div>
  );
}
